import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { fromEvent, merge } from 'rxjs'
import { useDraggable, UseDraggableOptions } from 'hooks/use-draggable'
import { useHoverState } from 'hooks/hover-notify'
import * as Duration from 'dration'
import { useCallRootViewModel } from 'contexts/CallRootContext'
import { VideoCall } from 'messaging/calling/VideoCall'
import CallViewModel from 'messaging/calling/CallViewModel'
import Avatar from 'components/Avatar'
import ErrorBlock from 'components/ErrorBlock'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import cx from 'classnames'
import styles from './CallManager.sass'

interface CallManagerProps {
  call: VideoCall
  positionManagement: UseDraggableOptions
}

const enum ButtonContext {
  Hangup = 'hangup',
  Return = 'return',
  Mute = 'mute',
  Unmute = 'unmute',
}

const contextDialogue = (context: ButtonContext | null) => {
  switch (context) {
    case ButtonContext.Hangup:
      return 'Leave call'
    case ButtonContext.Return:
      return 'Return to job'
    case ButtonContext.Mute:
      return 'Mute microphone'
    case ButtonContext.Unmute:
      return 'Unmute microphone'
    default:
      return null
  }
}

/**
 * CallManager, for managing an ongoing call.
 */
export default observer(function CallManager({
  call,
  positionManagement,
}: CallManagerProps) {
  const vm = useCallRootViewModel().callViewModelFor(call)
  const [isDragging, dragHandleRef] =
    useDraggable<HTMLDivElement>(positionManagement)
  const [context, setContext] = useState<ButtonContext | null>(null)
  const onContextChange = (context: ButtonContext | null) => {
    setContext(context)
  }

  return (
    <div
      className={cx(styles.root, isDragging && styles.dragging)}
      ref={dragHandleRef}
    >
      {vm.fetch.match({
        pending: () => (
          <div className={styles.fetching}>
            <Spinner />
          </div>
        ),
        rejected: (err) => <ErrorBlock error={err} />,
        resolved: () => (
          <>
            <div className={styles.tab}>
              <Icon size={12} name="drag" />
            </div>
            <div className={styles.main}>
              <CallStatus vm={vm} context={context} />
              <CallActions vm={vm} onContextChange={onContextChange} />
            </div>
          </>
        ),
      })}
    </div>
  )
})

interface CallStatusProps {
  vm: CallViewModel
  context: ButtonContext | null
}

/**
 * CallStatus, for displaying the status of an ongoing call.
 */
const CallStatus = observer(function CallStatus({
  vm,
  context,
}: CallStatusProps) {
  const speaking = vm.centerOfAttention.speaking
  const participants = speaking
    ? vm.centerOfAttention.participant.member
    : vm.call.otherParticipants.map((p) => p.member)
  const titleDialogueText = () => {
    if (speaking) {
      return `${vm.centerOfAttention.participant.member.givenName} is talking`
    }
    return vm.call.otherParticipants.length === 1
      ? `On call with ${vm.call.otherParticipants[0].member.givenName}`
      : `On call with ${vm.call.otherParticipants.length} other participants`
  }
  const contextDialogueText = contextDialogue(context)
  const noContext = contextDialogueText === null

  return (
    <div className={styles.status}>
      <div className={cx(styles.info, !noContext && styles.hidden)}>
        <div className={styles.participants}>
          <div className={cx(styles.speaker, speaking && styles.speaking)}>
            <Avatar size={40} subject={participants} />
          </div>
        </div>
        <div className={styles.details}>
          <div className={styles.title}>{titleDialogueText()}</div>
          <div className={styles.subtitle}>{Duration.format(vm.duration)}</div>
        </div>
      </div>
      <div className={cx(styles.context, noContext && styles.hidden)}>
        {contextDialogueText}
      </div>
    </div>
  )
})

interface CallActionsProps {
  vm: CallViewModel
  onContextChange: (context: ButtonContext | null) => void
}

/**
 * CallActions, for providing actions the user can perform during an ongoing call.
 */
const CallActions = observer(function CallActions({
  vm,
  onContextChange,
}: CallActionsProps) {
  const muted = vm.currentUserParticipant?.media?.muted

  const returnRef = useRef<HTMLButtonElement>(null)
  const muteRef = useRef<HTMLButtonElement>(null)
  const hangupRef = useRef<HTMLButtonElement>(null)

  const isHoveringReturn = useHoverState(returnRef)
  const isHoveringMute = useHoverState(muteRef)
  const isHoveringHangup = useHoverState(hangupRef)

  useEffect(() => {
    const muteContext = muted ? ButtonContext.Unmute : ButtonContext.Mute
    switch (true) {
      case isHoveringReturn:
        onContextChange(ButtonContext.Return)
        break
      case isHoveringMute:
        onContextChange(muteContext)
        break
      case isHoveringHangup:
        onContextChange(ButtonContext.Hangup)
        break
      default:
        onContextChange(null)
    }
  }, [muted, isHoveringReturn, isHoveringMute, isHoveringHangup])

  useEffect(() => {
    if (!returnRef.current || !muteRef.current || !hangupRef.current) {
      return
    }

    const hoverSubscription = merge(
      fromEvent<MouseEvent>(returnRef.current, 'mousedown'),
      fromEvent<MouseEvent>(muteRef.current, 'mousedown'),
      fromEvent<MouseEvent>(hangupRef.current, 'mousedown')
    ).subscribe((event) => event.stopImmediatePropagation())

    return () => {
      hoverSubscription.unsubscribe()
    }
  }, [])

  return (
    <div className={styles.actions}>
      <button
        className={styles.action}
        ref={returnRef}
        onClick={vm.revealCallContext}
      >
        <Icon size={20} name="call-return" />
      </button>
      <div className={styles.divider} />
      <button
        className={styles.action}
        ref={muteRef}
        onClick={vm.toggleMicrophone}
      >
        {muted ? (
          <Icon size={20} name="call-mic-off" />
        ) : (
          <Icon size={20} name="call-mic-on" />
        )}
      </button>
      <button
        className={cx(styles.action, styles.danger)}
        ref={hangupRef}
        onClick={vm.hangup}
      >
        <Icon size={20} name="call-end-call" />
      </button>
    </div>
  )
})
